/**
 * MHR Budget Management Page
 *
 * Admin-only page for managing site budget configuration,
 * reviewing approval requests, and viewing usage history.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  DollarSign,
  FileText,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '../../ui';
import * as mhrApi from '../lib/api';
import type {
  BudgetConfig,
  BudgetConfigFull,
  BudgetApproval,
  MonthlyUsage,
  HistoricalUsageMonth,
} from '../lib/api';

export function BudgetManagement() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Data
  const [config, setConfig] = useState<BudgetConfigFull | null>(null);
  const [usage, setUsage] = useState<MonthlyUsage | null>(null);
  const [approvals, setApprovals] = useState<BudgetApproval[]>([]);
  const [history, setHistory] = useState<HistoricalUsageMonth[]>([]);

  // Config form
  const [formConfig, setFormConfig] = useState({
    per_review_limit: '',
    admin_per_review_limit: '',
    monthly_budget: '',
    monthly_warning_threshold: '',
  });
  const [saving, setSaving] = useState(false);

  // Approval notes
  const [activeNotes, setActiveNotes] = useState<Record<string, string>>({});
  const [showNotes, setShowNotes] = useState<string | null>(null);
  const [processingApproval, setProcessingApproval] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [configData, usageData, approvalsData, historyData] = await Promise.all([
        mhrApi.getBudgetConfig(),
        mhrApi.getMonthlyUsage(),
        mhrApi.getApprovals(),
        mhrApi.getHistoricalUsage(),
      ]);

      const fullConfig = configData as BudgetConfigFull;
      setConfig(fullConfig);
      setUsage(usageData);
      setApprovals(approvalsData);
      setHistory(historyData);

      setFormConfig({
        per_review_limit: String(fullConfig.per_review_limit ?? 5),
        admin_per_review_limit: String(fullConfig.admin_per_review_limit ?? 25),
        monthly_budget: String(fullConfig.monthly_budget ?? 500),
        monthly_warning_threshold: String(fullConfig.monthly_warning_threshold ?? 80),
      });

      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load budget data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const updated = await mhrApi.updateBudgetConfig({
        per_review_limit: parseFloat(formConfig.per_review_limit),
        admin_per_review_limit: parseFloat(formConfig.admin_per_review_limit),
        monthly_budget: parseFloat(formConfig.monthly_budget),
        monthly_warning_threshold: parseFloat(formConfig.monthly_warning_threshold),
      });
      setConfig(updated);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save budget config');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (id: string) => {
    setProcessingApproval(id);
    try {
      await mhrApi.approveRequest(id, activeNotes[id]);
      setApprovals((prev) => prev.filter((a) => a.id !== id));
      setShowNotes(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessingApproval(null);
    }
  };

  const handleDeny = async (id: string) => {
    setProcessingApproval(id);
    try {
      await mhrApi.denyRequest(id, activeNotes[id]);
      setApprovals((prev) => prev.filter((a) => a.id !== id));
      setShowNotes(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to deny request');
    } finally {
      setProcessingApproval(null);
    }
  };

  const spentPercent =
    config && usage ? Math.min(100, (usage.total_cost / (config.monthly_budget || 1)) * 100) : 0;

  const getUsageColor = (pct: number) => {
    if (pct >= 80) return 'bg-red-500';
    if (pct >= 60) return 'bg-amber-500';
    return 'bg-green-500';
  };

  const getUsageTextColor = (pct: number) => {
    if (pct >= 80) return 'text-red-600 dark:text-red-400';
    if (pct >= 60) return 'text-amber-600 dark:text-amber-400';
    return 'text-green-600 dark:text-green-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error alert */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 dark:hover:text-red-400">
            &times;
          </button>
        </div>
      )}

      {/* Header */}
      <PageHeader
        title="Budget Management"
        icon={DollarSign}
        actions={
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Monthly Spend */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Monthly Spend</h3>
          </div>
          <div className="mb-2">
            <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${spentPercent >= 80 ? 'bg-amber-500' : 'bg-primary-500'}`}
                style={{ width: `${spentPercent}%` }}
              />
            </div>
          </div>
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            ${(usage?.total_cost ?? 0).toFixed(2)}{' '}
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
              / ${(config?.monthly_budget ?? 0).toFixed(2)}
            </span>
          </p>
        </div>

        {/* Reviews Completed */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Reviews Completed</h3>
          </div>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{usage?.reviews_completed ?? 0}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{usage?.total_pages_processed ?? 0} pages processed</p>
        </div>

        {/* Per-Review Limits */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Per-Review Limits</h3>
          </div>
          <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            ${(config?.per_review_limit ?? 5).toFixed(2)}{' '}
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">basic</span>
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ${(config?.admin_per_review_limit ?? 25).toFixed(2)} admin
          </p>
        </div>
      </div>

      {/* Budget Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Budget Configuration</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Per-Review Limit ($)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formConfig.per_review_limit}
              onChange={(e) => setFormConfig((p) => ({ ...p, per_review_limit: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Admin Per-Review Limit ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formConfig.admin_per_review_limit}
              onChange={(e) =>
                setFormConfig((p) => ({ ...p, admin_per_review_limit: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Monthly Budget ($)</label>
            <input
              type="number"
              step="1"
              min="0"
              value={formConfig.monthly_budget}
              onChange={(e) => setFormConfig((p) => ({ ...p, monthly_budget: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Warning Threshold (%)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={formConfig.monthly_warning_threshold}
              onChange={(e) =>
                setFormConfig((p) => ({ ...p, monthly_warning_threshold: e.target.value }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveConfig}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </button>
        </div>
      </div>

      {/* Pending Approvals */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Pending Approvals
          {approvals.length > 0 && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300">
              {approvals.length}
            </span>
          )}
        </h2>
        {approvals.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">No pending approval requests</p>
        ) : (
          <div className="space-y-4">
            {approvals.map((approval) => (
              <div key={approval.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {approval.requester?.full_name || 'Unknown User'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{approval.requester?.email}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                      Estimated cost:{' '}
                      <span className="font-medium">
                        ${approval.estimate?.total_estimated_cost?.toFixed(2) ?? '0.00'}
                      </span>
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      {new Date(approval.requested_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        if (showNotes === approval.id) {
                          handleApprove(approval.id);
                        } else {
                          setShowNotes(approval.id);
                        }
                      }}
                      disabled={processingApproval === approval.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 disabled:opacity-50 min-h-[44px] sm:min-h-0"
                    >
                      <CheckCircle className="h-3.5 w-3.5" />
                      Approve
                    </button>
                    <button
                      onClick={() => {
                        if (showNotes === approval.id) {
                          handleDeny(approval.id);
                        } else {
                          setShowNotes(approval.id);
                        }
                      }}
                      disabled={processingApproval === approval.id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 min-h-[44px] sm:min-h-0"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                      Deny
                    </button>
                  </div>
                </div>
                {showNotes === approval.id && (
                  <div className="mt-3">
                    <textarea
                      placeholder="Add notes (optional)..."
                      value={activeNotes[approval.id] || ''}
                      onChange={(e) =>
                        setActiveNotes((prev) => ({ ...prev, [approval.id]: e.target.value }))
                      }
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      rows={2}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage History */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Usage History</h2>
        {history.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">No usage history yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 pr-4 font-medium text-gray-500 dark:text-gray-400">Month</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-500 dark:text-gray-400">Reviews</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-500 dark:text-gray-400">Pages</th>
                  <th className="text-right py-2 px-4 font-medium text-gray-500 dark:text-gray-400">Cost</th>
                  <th className="text-right py-2 pl-4 font-medium text-gray-500 dark:text-gray-400">Budget Used</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => {
                  const budgetPct =
                    row.budget_limit > 0
                      ? Math.min(100, (row.total_cost / row.budget_limit) * 100)
                      : 0;
                  return (
                    <tr key={row.year_month} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">{row.year_month}</td>
                      <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300">
                        {row.reviews_completed}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300">
                        {row.total_pages_processed}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-700 dark:text-gray-300">
                        ${row.total_cost.toFixed(2)}
                      </td>
                      <td className="py-3 pl-4">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${getUsageColor(budgetPct)}`}
                              style={{ width: `${budgetPct}%` }}
                            />
                          </div>
                          <span className={`text-xs font-medium ${getUsageTextColor(budgetPct)}`}>
                            {budgetPct.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default BudgetManagement;
